import type {
  CreateDataSourceBody,
  DataSourceSummary,
  QueryDataSourceBody,
  UpdateDataSourceBody,
} from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dataSourcesApi } from '@/api/data-sources';
import { useToast } from '@/components/ui/toast';

export const dataSourceKeys = {
  summaries: ['data-sources', 'summaries'] as const,
  all: ['data-sources', 'all'] as const,
  run: (key: string, body: QueryDataSourceBody) => ['data-sources', 'run', key, body] as const,
};

/**
 * Every page in the shell asks for these, so they are cached for a while:
 * the configuration changes about once a quarter, not once a minute.
 */
export function useDataSourceSummaries() {
  return useQuery({ queryKey: dataSourceKeys.summaries, queryFn: dataSourcesApi.summaries, staleTime: 5 * 60_000 });
}

export function useDataSourceSummary(key: string | undefined): DataSourceSummary | undefined {
  const { data } = useDataSourceSummaries();
  return key ? data?.find((s) => s.key === key) : undefined;
}

/**
 * One run of a configured list. Live per page load, as asked — but not on
 * every keystroke: the caller debounces the filters into `body`.
 */
export function useDataSourceRun(key: string | undefined, body: QueryDataSourceBody, enabled = true) {
  return useQuery({
    queryKey: dataSourceKeys.run(key ?? '', body),
    queryFn: () => dataSourcesApi.run(key as string, body),
    enabled: Boolean(key) && enabled,
    // It is someone else's API: keep the previous rows on screen while the
    // next page loads rather than flashing an empty table.
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    retry: false,
  });
}

export function useAllDataSources(enabled = true) {
  return useQuery({ queryKey: dataSourceKeys.all, queryFn: dataSourcesApi.all, enabled });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['data-sources'] });
  };
}

export function useCreateDataSource() {
  const toast = useToast();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CreateDataSourceBody) => dataSourcesApi.create(body),
    onSuccess: (source) => {
      invalidate();
      toast.success(
        `“${source.label}” created`,
        source.isEnabled ? undefined : 'It stays hidden from the team until you switch it on.',
      );
    },
  });
}

export function useUpdateDataSource() {
  const toast = useToast();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ key, body }: { key: string; body: UpdateDataSourceBody }) => dataSourcesApi.update(key, body),
    onSuccess: (source) => {
      invalidate();
      toast.success(`“${source.label}” saved`);
    },
  });
}

export function useDeleteDataSource() {
  const toast = useToast();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (key: string) => dataSourcesApi.remove(key),
    onSuccess: () => {
      invalidate();
      toast.success('List removed');
    },
  });
}
